import { Controller } from "@hotwired/stimulus"

// Infinite vertical drift. The track holds two identical copies of its content
// stacked; we translate from one copy-height up to 0 and loop, so the seam is
// invisible. Direction is downward, so the content appears to flow into the
// panel below. Static for reduced motion; re-measures on resize / font load.
export default class extends Controller {
  static targets = ["track"]
  static values = { speed: { type: Number, default: 20 } } // pixels per second

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.visible = true
    this.start = this.start.bind(this)
    requestAnimationFrame(this.start)
    if (document.fonts) document.fonts.ready.then(this.start)
    window.addEventListener("resize", this.start)

    // Only animate while on screen.
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting
      if (!this.animation) return
      this.visible ? this.animation.play() : this.animation.pause()
    })
    this.observer.observe(this.element)
  }

  disconnect() {
    if (this.animation) this.animation.cancel()
    if (this.observer) this.observer.disconnect()
    window.removeEventListener("resize", this.start)
  }

  start() {
    const children = this.trackTarget.children
    if (children.length === 0) return

    // Distance from one copy to the next (includes the gap between them), so the
    // loop lands exactly on the identical second copy — seamless, no gap.
    const first = children[0].getBoundingClientRect()
    const distance =
      children.length >= 2 ? children[1].getBoundingClientRect().top - first.top : first.height
    if (distance <= 0) return

    if (this.animation) this.animation.cancel()
    this.animation = this.trackTarget.animate(
      [{ transform: `translateY(${-distance}px)` }, { transform: "translateY(0)" }],
      { duration: (distance / this.speedValue) * 1000, iterations: Infinity, easing: "linear" }
    )
    if (!this.visible) this.animation.pause()
  }
}
